import { Fragment, useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getGroupedRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Filter,
  ChevronDown,
  ChevronUp,
  Download,
  MoreVertical,
  Settings,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import { FiFilter } from "react-icons/fi";
import { Loading } from "../loading/Loading";
import { DateFilterModal } from "../modal/DateFilterModal";
import Exporters from "../../../utils/exporters";
import Style from "./index.module.css";

export default function DataTable({
  title,
  text,
  data,
  columns,
  actions = [],
  bulkActions = [],
  page,
  setPage,
  limit,
  setLimit,
  total,
  onColumnFilterChange,
  columnFilters,
  setColumnFilters,
  initialVisibleColumns,
  loading,
  handleFilterData,
  setDataInicio,
  dataInicio,
  setDataFim,
  dataFim,
  setShowModal,
  showModal,
  getRowStyle,
  getRowClassName,
  legend,
  renderExpandedRow,
  getRowId,
  enableRowSelection = true,
  expandOnDoubleClick = false,
  expandWithIndicatorOnly = false,
}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [grouping, setGrouping] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [openBulkActions, setOpenBulkActions] = useState(false);
  const [densePadding, setDensePadding] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [tempColumnFilters, setTempColumnFilters] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const toggleFilter = (headerId, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropdownOpen(filterDropdownOpen === headerId ? null : headerId);
    setFilterPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
  };

  const table = useReactTable({
    data: useMemo(() => {
      if (!globalFilter) return data;
      return data.filter(row =>
        columns.some(col => {
          const value = row[col.accessorKey];
          return value?.toString().toLowerCase().includes(globalFilter.toLowerCase());
        })
      );
    }, [data, globalFilter, columns]),
    columns: useMemo(() => {
      const expandColumn = {
        id: "expand",
        header: "",
        cell: ({ row }) => {
          const expandedRowId = getRowId ? getRowId(row.original) : row.id;
          const isExpanded = !!expandedRows[expandedRowId];

          return (
            <button
              type="button"
              className={`${Style.expandIndicator} ${isExpanded ? Style.expandIndicatorActive : ""}`}
              onClick={(event) => {
                event.stopPropagation();
                toggleExpandedRow(row);
              }}
              title={isExpanded ? "Ocultar itens" : "Visualizar itens"}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        size: 44,
      };

      const selectColumn = {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className={Style.checkbox}
            onClick={(event) => event.stopPropagation()}
            {...{
              checked: table.getIsAllPageRowsSelected(),
              indeterminate: table.getIsSomePageRowsSelected(),
              onChange: table.getToggleAllPageRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className={Style.checkbox}
            onClick={(event) => event.stopPropagation()}
            {...{
              checked: row.getIsSelected(),
              disabled: row.getIsGrouped(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
        size: 40,
      };

      const actionsColumn = {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className={Style.actionButtons}>
            {actions.map(({ icon: Icon, onClick, title, color = "default" }, index) => (
              <button
                key={index}
                className={`${Style.controlButton} ${Style[color]}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onClick(row.original);
                }}
                title={title}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 80,
      };

      const tableColumns = [
        ...(renderExpandedRow && expandWithIndicatorOnly ? [expandColumn] : []),
        ...(enableRowSelection ? [selectColumn] : []),
        actionsColumn,
        ...columns.map(col => ({
        ...col,
        filterFn: "includesString",
        meta: {
          filterVariant: col.filterVariant || 'text',
          filterOptions: col.filterOptions || null
        }
      }))];

      return tableColumns;
    }, [columns, actions, enableRowSelection, expandedRows, expandWithIndicatorOnly, getRowId, renderExpandedRow]),
    manualFiltering: true,
    manualPagination: true,
    pageCount: Math.ceil(total / limit),
    state: {
      globalFilter,
      columnVisibility,
      sorting,
      grouping,
      rowSelection,
      pagination: { pageIndex: page - 1, pageSize: limit },
      columnFilters
    },
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex: page - 1, pageSize: limit }) : updater;
      setPage(next.pageIndex + 1);
      setLimit(next.pageSize);
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    globalFilterFn: "includesString",
  });


  useEffect(() => {
    if (!initialVisibleColumns || initialVisibleColumns.length === 0) return;

    table.getAllLeafColumns().forEach(col => {
      const shouldShow =
        initialVisibleColumns.includes(col.id) ||
        col.id === "select" ||
        col.id === "actions" ||
        col.id === "expand";

      col.toggleVisibility(shouldShow);
    });
  }, [table, initialVisibleColumns]);



  const selectedRows = useMemo(() => {
    return Object.keys(rowSelection)
      .filter(id => rowSelection[id])
      .map(id => table.getRow(id).original);
  }, [rowSelection, table]);

  useEffect(() => {
    if (selectedRows.length === 0) {
      setOpenBulkActions(false);
    }
  }, [selectedRows.length]);

  const applyFilter = (headerId, value) => {
    const newFilters = columnFilters.filter(f => f.id !== headerId);

    if (value && value !== "") {
      newFilters.push({ id: headerId, value });
    }

    setColumnFilters(newFilters);

    if (onColumnFilterChange) {
      const filtrosApi = newFilters.map(f => ({
        campo: f.id,
        valor: f.value,
        uselike: true
      }));
      onColumnFilterChange(filtrosApi);
    }
  };

  const clearFilter = (headerId) => {
    const newFilters = columnFilters.filter(f => f.id !== headerId);
    setColumnFilters(newFilters);

    setTempColumnFilters(prev => ({ ...prev, [headerId]: "" }));

    if (onColumnFilterChange) {
      const filtrosApi = newFilters.map(f => ({
        campo: f.id,
        valor: f.value,
        uselike: true
      }));
      onColumnFilterChange(filtrosApi);
    }
  };

  const toggleExpandedRow = (row) => {
    if (!renderExpandedRow) return;

    const rowId = getRowId ? getRowId(row.original) : row.id;
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  return (
    <div className={Style.tableContainer}>
      {loading && <Loading text="Carregando dados..." />}

      <div className={Style.descriptionContainer}>
        <h2>{title}</h2>
        <p>{text}</p>
        {showModal !== null && (
          <div className={Style.aditionalFilters}>
            <FiFilter
              size={36}
              className={Style.iconFiltro}
              onClick={() => setShowModal(true)}
              title="Abrir filtros"
            />
          </div>
        )}
      </div>

      <div className={Style.toolbar}>
        <div className={Style.toolbarLeft}>
          {enableRowSelection && bulkActions.length > 0 && selectedRows.length > 0 && (
            <div className={Style.bulkActions}>
              <button
                className={Style.bulkButton}
                onClick={() => setOpenBulkActions((prev) => !prev)}
              >
                Ações <ChevronDown size={16} />
              </button>

              {openBulkActions && (
                <div className={Style.bulkDropdown}>
                  <div className={Style.bulkDropdownHeader}>
                    {selectedRows.length} selecionado{selectedRows.length > 1 ? "s" : ""}
                  </div>
                  {bulkActions.map(({ icon: Icon, label, onClick }, index) => (
                    <button
                      key={index}
                      className={Style.bulkDropdownItem}
                      onClick={() => {
                        setOpenBulkActions(false);
                        onClick(selectedRows);
                      }}
                    >
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={Style.toolbarRight}>
          <div className={Style.searchWrapper}>
            <Search size={16} className={Style.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={globalFilter ?? ""}
              onChange={e => setGlobalFilter(e.target.value)}
              className={Style.globalSearch}
            />

            <div className={Style.toolbarActions}>
              <div className={Style.columnManager}>
                <button
                  className={Style.toolbarButton}
                  onClick={() => setShowColumnManager(!showColumnManager)}
                  title="Gerenciar colunas"
                >
                  <Settings size={16} />
                </button>

                {showColumnManager && (
                  <div className={Style.columnDropdown}>
                    <div className={Style.columnDropdownHeader}>
                      <span>Colunas</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowColumnManager(false);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className={Style.columnList}>
                      {table.getAllLeafColumns()
                        .filter(column => column.id !== "select" && column.id !== "actions" && column.id !== "expand")
                        .map(col => (
                          <label key={col.id} className={Style.columnToggle}>
                            <div className={Style.checkboxContainer}>
                              <input
                                type="checkbox"
                                checked={col.getIsVisible()}
                                onChange={col.getToggleVisibilityHandler()}
                              />
                              <span className={Style.checkmark}></span>
                            </div>
                            <span className={Style.columnName}>{col.columnDef.header}</span>
                          </label>
                        ))}
                    </div>
                    <div className={Style.columnDropdownFooter}>
                      <button
                        className={Style.denseButton}
                        onClick={() => setDensePadding(!densePadding)}
                      >
                        {densePadding ? "Modo normal" : "Modo compacto"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={Style.exportMenu}>
                <button
                  className={Style.toolbarButton}
                  onClick={() => setOpenExport(!openExport)}
                  title="Exportar dados"
                >
                  <Download size={16} />
                </button>

                {openExport && (
                  <div className={Style.exportDropdown}>
                    <div className={Style.exportDropdownHeader}>
                      Exportar como
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenExport(false);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className={Style.exportDropdownContainer}>
                      <button onClick={() => { Exporters.exportToExcel(data); setOpenExport(false); }} className={Style.exportItem}>
                        Excel (.xlsx)
                      </button>
                      <button onClick={() => { Exporters.exportToCSV(data); setOpenExport(false); }} className={Style.exportItem}>
                        CSV (.csv)
                      </button>
                      <button onClick={() => { Exporters.exportToPDF(table); setOpenExport(false); }} className={Style.exportItem}>
                        PDF (.pdf)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={Style.tableWrapper}>
        {showModal && (
          <DateFilterModal
            show={showModal}
            dataInicio={dataInicio}
            dataFim={dataFim}
            onClose={() => setShowModal(false)}
            onSend={handleFilterData}
            setDataFim={setDataFim}
            setDataInicio={setDataInicio}
          />
        )}

        {enableRowSelection && <span className={Style.selectedCount}>{selectedRows.length} selecionado(s)</span>}
        <table className={`${Style.table} ${densePadding ? Style.dense : ""}`}>
          <thead className={Style.tableHeader}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{ width: header.column.columnDef.size }}
                    className={header.column.getCanSort() ? Style.sortableHeader : ""}
                  >
                    <div
                      className={Style.headerCell}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className={Style.headerText} title={header.column.columnDef.header}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>

                      {header.column.getCanSort() && (
                        <span className={Style.sortIcon}>
                          {{
                            asc: <ChevronUp size={14} />,
                            desc: <ChevronDown size={14} />,
                          }[header.column.getIsSorted()] ?? <MoreVertical size={14} />}
                        </span>
                      )}

                      {header.column.getCanFilter() && (
                        <div className={Style.filterWrapper}>
                          <button
                            className={`${Style.filterButton} ${header.column.getFilterValue() ? Style.activeFilter : ""}`}
                            onClick={e => toggleFilter(header.id, e)}
                            title="Filtrar"
                          >
                            <Filter size={14} />
                          </button>
                          {filterDropdownOpen === header.id && (
                            <div className={Style.filterDropdown} style={{ top: filterPosition.top, left: filterPosition.left }} onClick={(e) => e.stopPropagation()}>
                              <div className={Style.filterHeader}>
                                <span>Filtrar por {header.column.columnDef.header.slice(0, 12)}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilterDropdownOpen(null);
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <div className={Style.filterContent}>
                                {header.column.columnDef.meta?.filterVariant === 'select' ? (
                                  <select
                                    className={Style.filterSelect}
                                    value={tempColumnFilters[header.id] ?? ""}
                                    onChange={e => {
                                      const value = e.target.value || "";
                                      setTempColumnFilters(prev => ({ ...prev, [header.id]: value }));
                                    }}
                                  >
                                    <option value="">Todos</option>
                                    {header.column.columnDef.meta?.filterOptions?.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    className={Style.filterInput}
                                    placeholder={`Filtrar ${header.column.columnDef.header.slice(0, 12)}...`}
                                    value={tempColumnFilters[header.id] ?? ""}
                                    onChange={e => {
                                      const value = e.target.value || "";
                                      setTempColumnFilters(prev => ({ ...prev, [header.id]: value }));
                                    }}
                                  />
                                )}
                              </div>
                              <div className={Style.filterFooter}>
                                <button
                                  className={Style.filterApply}
                                  onClick={() => {
                                    const value = tempColumnFilters[header.id] || undefined;
                                    applyFilter(header.id, value);
                                    setFilterDropdownOpen(null);
                                  }}
                                >
                                  Aplicar
                                </button>

                                <button
                                  className={Style.filterClear}
                                  onClick={() => {
                                    clearFilter(header.id);
                                    setFilterDropdownOpen(null);
                                  }}
                                >
                                  Limpar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => {
                const expandedRowId = getRowId ? getRowId(row.original) : row.id;
                const isExpanded = !!expandedRows[expandedRowId];

                return (
                  <Fragment key={row.id}>
                    <tr
                      className={`${Style.tableRow} ${renderExpandedRow && !expandOnDoubleClick && !expandWithIndicatorOnly ? Style.clickableRow : ""} ${renderExpandedRow && expandOnDoubleClick && !expandWithIndicatorOnly ? Style.doubleClickRow : ""} ${isExpanded ? Style.expandedTableRow : ""} ${isExpanded && expandOnDoubleClick && !expandWithIndicatorOnly ? Style.expandedCursorRow : ""} ${row.getIsSelected() ? Style.selectedRow : ""} ${getRowClassName ? getRowClassName(row.original) : ""
                        }`}
                      style={getRowStyle ? getRowStyle(row.original) : {}}
                      onClick={() => !expandOnDoubleClick && !expandWithIndicatorOnly && toggleExpandedRow(row)}
                      onDoubleClick={() => expandOnDoubleClick && !expandWithIndicatorOnly && toggleExpandedRow(row)}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className={Style.tableCell} data-label={cell.column.columnDef.header}>
                          {row.getIsGrouped() ? (
                            <span onClick={row.getToggleExpandedHandler()} className={Style.groupRow}>
                              {row.getIsExpanded() ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              {flexRender(cell.column.columnDef.cell, cell.getContext())} ({row.subRows.length})
                            </span>
                          ) : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && (
                      <tr className={Style.expandedContentRow}>
                        <td colSpan={table.getVisibleLeafColumns().length} className={Style.expandedContentCell}>
                          {renderExpandedRow(row.original)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={table.getAllColumns().length} className={Style.noData}>
                  Nenhum registro encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {legend?.length > 0 && (
        <div className={Style.legendContainer}>
          {legend.map((item, idx) => (
            <div key={idx} className={Style.legendItem}>
              <span className={Style.legendColor} style={{ backgroundColor: item.color }}></span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
      <div className={Style.pagination}>
        <div className={Style.paginationControls}>
          <span className={Style.rowsPerPage}>
            Linhas por página:
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={e => table.setPageSize(Number(e.target.value))}
            className={Style.pageSizeSelect}
          >
            {[
              { label: "5", value: 5 },
              { label: "10", value: 10 },
              { label: "30", value: 30 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
              { label: "200", value: 200 },
            ].map(obj => (
              <option key={obj.value} value={obj.value}>
                {obj.label}
              </option>
            ))}
          </select>

          <span className={Style.pageInfo}>
            Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total} registros
          </span>

          <div className={Style.pageButtons}>
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className={Style.pageButton}
              title="Primeira página"
            >
              {isMobile ? <ChevronsLeft size={16} /> : "«"}
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className={Style.pageButton}
              title="Página anterior"
            >
              {isMobile ? <ChevronLeft size={16} /> : "‹"}
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className={Style.pageButton}
              title="Próxima página"
            >
              {isMobile ? <ChevronRight size={16} /> : "›"}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className={Style.pageButton}
              title="Última página"
            >
              {isMobile ? <ChevronsRight size={16} /> : "»"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
